import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { useApp } from "@/context/AppContext";
import { listUsers, createUser, deleteUser, resendWelcome, resendWelcomeBulk, adminParticipantProgress, adminBulkOnboard, formatApiErrorDetail } from "@/lib/api";
import { Mail, Trash2, BarChart3, X, UserPlus, Send, Upload } from "lucide-react";
import { toast } from "sonner";

const FILTERS = [["all", "All"], ["participant", "Participants"], ["trainer", "Trainers"], ["hr", "HR"], ["admin", "Admins"]];

function BulkImportModal({ onClose, onDone }) {
  const [groupName, setGroupName] = useState("");
  const [company, setCompany] = useState("");
  const [language, setLanguage] = useState("nl");
  const [sendWelcome, setSendWelcome] = useState(true);
  const [part2, setPart2] = useState(false);
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);

  const parse = () =>
    raw.split("\n").map((line) => {
      const parts = line.split(/[,;\t]/).map((s) => s.trim());
      const full_name = parts[0] || "";
      const emails = parts.filter((p) => p.includes("@")).map((p) => p.toLowerCase());
      const role = parts.find((p) => ["participant", "trainer", "admin", "hr"].includes(p.toLowerCase()));
      return { full_name, email: emails[0] || "", alt_emails: emails.slice(1), role: role ? role.toLowerCase() : "participant" };
    }).filter((p) => p.full_name && p.email);

  const participants = parse();

  const submit = async (e) => {
    e.preventDefault();
    if (!groupName.trim() || participants.length === 0) {
      toast.error("Enter a group name and at least one valid 'Name, email' line");
      return;
    }
    setBusy(true);
    try {
      const r = await adminBulkOnboard({ group_name: groupName, company, language, send_welcome: sendWelcome, part2, participants });
      toast.success(`Group ready: ${r.created} created, ${r.skipped} already existed, ${r.emailed} welcome emails sent`);
      onDone();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="rbl-modal-bg" onClick={onClose}>
      <div className="rbl-modal" onClick={(e) => e.stopPropagation()} data-testid="bulk-import-modal" style={{ maxWidth: 620 }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <h3 style={{ margin: 0 }}>Bulk import group</h3>
          <button className="rbl-link" onClick={onClose} style={{ border: "none", background: "none" }}><X size={18} /></button>
        </div>
        <form onSubmit={submit}>
          <div style={{ display: "flex", gap: 10 }}>
            <div className="rbl-field" style={{ flex: 1 }}><label className="rbl-label">Group name</label>
              <input className="rbl-input" data-testid="bulk-group-name" required value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="MPG Card Finishing 2024" /></div>
            <div className="rbl-field" style={{ flex: 1 }}><label className="rbl-label">Company</label>
              <input className="rbl-input" data-testid="bulk-company" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Millennium Print Group" /></div>
          </div>
          <div className="rbl-field"><label className="rbl-label">Participants — one per line: <em>Full name, email[, existing email][, role]</em></label>
            <textarea className="rbl-input" data-testid="bulk-roster" rows={8} value={raw} onChange={(e) => setRaw(e.target.value)}
              placeholder={"Illia Holovatyi, silvertx777@gmail.com\nGyokhan Shakirov, g.shakirov@mgb.nl, gyokhan@live.com"} style={{ fontFamily: "monospace", fontSize: 13 }} /></div>
          <p style={{ color: "var(--muted)", fontSize: 12, margin: "0 0 8px" }}>A 2nd email is only used to find an existing account (no duplicate is created).</p>
          <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
            <div className="rbl-field" style={{ margin: 0 }}><label className="rbl-label">Default language</label>
              <select className="rbl-input" value={language} onChange={(e) => setLanguage(e.target.value)} data-testid="bulk-language">
                <option value="nl">NL</option><option value="en">EN</option><option value="bg">BG</option><option value="uk">UK</option>
              </select></div>
            <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14, marginTop: 18 }}>
              <input type="checkbox" data-testid="bulk-send-welcome" checked={sendWelcome} onChange={(e) => setSendWelcome(e.target.checked)} /> Send welcome emails now
            </label>
            <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14, marginTop: 18 }}>
              <input type="checkbox" data-testid="bulk-part2" checked={part2} onChange={(e) => setPart2(e.target.checked)} /> Part 2 access
            </label>
          </div>
          <p style={{ color: "var(--muted)", fontSize: 13, marginTop: 6 }}>{participants.length} valid participant(s) detected.</p>
          <button className="rbl-btn block" disabled={busy} data-testid="bulk-submit">{busy ? "Importing…" : `Create group & onboard ${participants.length}`}</button>
        </form>
      </div>
    </div>
  );
}

function AddUserModal({ onClose, onCreated }) {
  const [f, setF] = useState({ full_name: "", email: "", role: "participant", language: "nl" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await createUser(f);
      toast.success(r.email_sent ? "User created — welcome email sent" : "User created (email not sent)");
      onCreated();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="rbl-modal-bg" onClick={onClose}>
      <div className="rbl-modal" onClick={(e) => e.stopPropagation()} data-testid="add-user-modal">
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <h3 style={{ margin: 0 }}>Add user</h3>
          <button className="rbl-link" onClick={onClose} style={{ border: "none", background: "none" }}><X size={18} /></button>
        </div>
        <form onSubmit={submit}>
          <div className="rbl-field"><label className="rbl-label">Full name</label>
            <input className="rbl-input" data-testid="newuser-name" required value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} /></div>
          <div className="rbl-field"><label className="rbl-label">Email</label>
            <input className="rbl-input" type="email" data-testid="newuser-email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
          <div className="rbl-field"><label className="rbl-label">Role</label>
            <select className="rbl-input" data-testid="newuser-role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>
              <option value="participant">Participant</option><option value="trainer">Trainer</option><option value="hr">HR</option><option value="admin">Admin</option>
            </select></div>
          <div className="rbl-field"><label className="rbl-label">Language</label>
            <select className="rbl-input" value={f.language} onChange={(e) => setF({ ...f, language: e.target.value })}>
              <option value="nl">NL</option><option value="en">EN</option><option value="bg">BG</option><option value="uk">UK</option>
            </select></div>
          <button className="rbl-btn block" disabled={busy} data-testid="newuser-submit">{busy ? "…" : "Create & send welcome email"}</button>
        </form>
      </div>
    </div>
  );
}

function ProgressDrawer({ userId, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { adminParticipantProgress(userId).then(setD).catch(() => setD(false)); }, [userId]);
  return (
    <div className="rbl-modal-bg" onClick={onClose}>
      <div className="rbl-drawer" onClick={(e) => e.stopPropagation()} data-testid="progress-drawer">
        <button className="rbl-link" onClick={onClose} style={{ border: "none", background: "none", float: "right" }}><X size={18} /></button>
        {!d && <div className="rbl-spinner" />}
        {d && (
          <>
            <h2 style={{ marginTop: 0 }}>{d.user.full_name}</h2>
            <div style={{ color: "var(--muted)" }}>{d.user.email} · {d.user.role}</div>
            <div style={{ margin: "8px 0" }}>
              <span className={`rbl-badge ${d.user.has_logged_in ? "ok" : "todo"}`}>{d.user.has_logged_in ? "✓ Ingelogd" : "⏳ Nog niet ingelogd"}</span>
              {d.groups[0] && <span className="rbl-badge muted" style={{ marginLeft: 8 }}>{d.groups[0].name}</span>}
            </div>
            <div className="rbl-stat-grid" style={{ marginTop: 14 }}>
              <div className="rbl-card"><span className="rbl-card-num">WORKSHEETS</span><span style={{ fontSize: 28, fontWeight: 800, color: "var(--navy)" }}>{d.worksheets_done}/{d.worksheets.length}</span></div>
              <div className="rbl-card"><span className="rbl-card-num">QUIZZES</span><span style={{ fontSize: 28, fontWeight: 800, color: "var(--navy)" }}>{d.quizzes_done}/{d.quizzes.length}</span></div>
              <div className="rbl-card"><span className="rbl-card-num">REFLECTIONS</span><span style={{ fontSize: 28, fontWeight: 800, color: "var(--navy)" }}>{d.reflections.length}</span></div>
            </div>
            <h4>Quizzes</h4>
            {d.quizzes.map((q) => (
              <div key={q.id} className="rbl-list-item"><span>{q.title}</span>{q.done ? <span className="rbl-badge ok">{q.score}%</span> : <span className="rbl-badge todo">⏳</span>}</div>
            ))}
            <h4>Latest reflections</h4>
            {d.reflections.length === 0 && <p style={{ color: "var(--muted)" }}>None yet.</p>}
            {d.reflections.slice(0, 3).map((r) => (
              <div key={r.id} className="rbl-list-item" style={{ display: "block" }}>
                {r.prompt_text && <div style={{ color: "var(--navy)", fontWeight: 700, fontSize: 13 }}>{r.prompt_text}</div>}
                <div style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>{r.text}</div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const { user: me } = useApp();
  const isAdmin = me?.role === "admin";
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [drawer, setDrawer] = useState(null);

  const load = () => listUsers().then(setUsers).catch(() => {});
  useEffect(() => { load(); }, []);

  const filtered = users.filter((u) => filter === "all" || u.role === filter);

  const doResend = async (u) => {
    if (!window.confirm(`Resend welcome email to ${u.email}?`)) return;
    try { await resendWelcome(u.id); toast.success("Welcome email sent"); } catch { toast.error("Failed"); }
  };
  const doDelete = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try { await deleteUser(u.id); toast.success("User deleted"); load(); } catch { toast.error("Failed"); }
  };
  const doBulk = async () => {
    const never = users.filter((u) => u.role === "participant" && !u.has_logged_in).length;
    if (!window.confirm(`Send the welcome invite to all ${never} participants who haven't logged in yet?`)) return;
    try { const r = await resendWelcomeBulk(null); toast.success(`Sent ${r.sent}/${r.attempted} invites`); }
    catch { toast.error("Failed"); }
  };

  return (
    <AdminLayout>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <h1 className="rbl-h1" style={{ fontSize: 34 }}>Users</h1>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="rbl-btn" onClick={doBulk} data-testid="bulk-resend"><Send size={16} /> Invite all who haven't logged in</button>
          <button className="rbl-btn" onClick={() => setShowBulk(true)} data-testid="bulk-import-btn"><Upload size={16} /> Bulk import group</button>
          <button className="rbl-btn" onClick={() => setShowAdd(true)} data-testid="add-user-btn"><UserPlus size={16} /> Add user</button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, margin: "18px 0" }}>
        {FILTERS.map(([k, label]) => (
          <button key={k} className={`rbl-lang-btn ${filter === k ? "active" : ""}`} data-testid={`filter-${k}`} onClick={() => setFilter(k)}>{label}</button>
        ))}
      </div>

      <div data-testid="users-table">
        {filtered.map((u) => (
          <div key={u.id} className="rbl-list-item" data-testid={`user-row-${u.id}`}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700 }}>{u.full_name} <span style={{ color: "var(--muted)", fontWeight: 400 }}>· {u.role}</span></div>
              <div style={{ color: "var(--muted)", fontSize: 13 }}>{u.email}</div>
            </div>
            <span className={`rbl-badge ${u.has_logged_in ? "ok" : "todo"}`}>{u.has_logged_in ? "✓ Ingelogd" : "⏳ Nog niet ingelogd"}</span>
            <div style={{ display: "flex", gap: 6, marginLeft: 8 }}>
              {u.role === "participant" && <button className="rbl-icon-btn" title="Progress" data-testid={`progress-${u.id}`} onClick={() => setDrawer(u.id)}><BarChart3 size={18} /></button>}
              <button className="rbl-icon-btn" title="Resend welcome" data-testid={`resend-${u.id}`} onClick={() => doResend(u)}><Mail size={18} /></button>
              {isAdmin && <button className="rbl-icon-btn danger" title="Delete" data-testid={`delete-${u.id}`} onClick={() => doDelete(u)}><Trash2 size={18} /></button>}
            </div>
          </div>
        ))}
      </div>

      {showAdd && <AddUserModal onClose={() => setShowAdd(false)} onCreated={load} />}
      {showBulk && <BulkImportModal onClose={() => setShowBulk(false)} onDone={load} />}
      {drawer && <ProgressDrawer userId={drawer} onClose={() => setDrawer(null)} />}
    </AdminLayout>
  );
}
